import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { http, gradeLabel } from "@/lib/api";
import { RecordShell, LetterBadge, GRADING_SCALE } from "@/components/records/RecordShell";

const Block = ({ label, children }) => (
  <section className="mt-8">
    <p className="text-xs uppercase tracking-widest text-stone-500 font-semibold">{label}</p>
    <div className="mt-3">{children}</div>
  </section>
);

const Metric = ({ value, label, testId }) => (
  <div className="rounded-2xl bg-[#F7F3EB] p-4">
    <p className="font-display text-2xl text-[#2C4A3E]" data-testid={testId}>{value}</p>
    <p className="text-xs text-stone-500 mt-1">{label}</p>
  </div>
);

export default function ReportCard() {
  const { studentId } = useParams();
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => { http.get(`/students/${studentId}/report-card`).then((r) => setD(r.data)).catch(() => setErr("You don't have access to this report card.")); }, [studentId]);
  if (err) return <p className="text-center py-20 text-stone-500" data-testid="record-error">{err}</p>;
  if (!d) return <p className="text-center py-20 text-stone-500">Loading…</p>;

  return (
    <RecordShell title="Report Card" back testId="report-card-page">
      <div className="flex flex-wrap justify-between gap-4 mt-6">
        <div>
          <h1 className="font-display text-3xl text-[#2C4A3E]" data-testid="report-student-name">{d.student.name}</h1>
          <p className="text-sm text-stone-500">{gradeLabel(d.student.grade)} · School year {d.school_year} (to date)</p>
          {d.classes.length > 0 && <p className="text-sm text-stone-500">Class: {d.classes.map((c) => c.name).join(", ")}</p>}
        </div>
        <div className="text-right">
          <p className="text-xs uppercase tracking-widest text-stone-500">Overall</p>
          <p className="font-display text-4xl text-[#2C4A3E]" data-testid="report-overall-average">{d.overall_average ?? "—"}</p>
          <LetterBadge letter={d.overall_letter} testId="report-overall-letter" />
        </div>
      </div>

      <Block label="Subject grades">
        <table className="w-full text-sm" data-testid="report-subjects-table">
          <thead><tr className="text-left text-xs text-stone-500 border-b border-[#E6DEC3]"><th className="py-2">Subject</th><th>Lessons</th><th>Average</th><th>Grade</th></tr></thead>
          <tbody>
            {d.subjects.map((s) => (
              <tr key={s.subject_id} className="border-b border-[#E6DEC3]/60" data-testid={`report-subject-${s.subject_id}`}>
                <td className="py-3 font-medium text-stone-800">{s.subject_name}</td><td>{s.lessons}</td><td>{s.average ?? "—"}</td>
                <td><LetterBadge letter={s.letter} /></td>
              </tr>
            ))}
            {!d.subjects.length && <tr><td colSpan={4} className="py-6 text-center text-stone-500">No graded lessons yet this school year.</td></tr>}
          </tbody>
        </table>
        <p className="text-xs text-stone-400 mt-2">{GRADING_SCALE}</p>
      </Block>

      <div className="grid sm:grid-cols-2 gap-8">
        <Block label="Attendance">
          <div className="grid grid-cols-2 gap-3">
            <Metric value={d.attendance.days_present} label={`Days present (≥ ${d.attendance.goal_minutes} min)`} testId="report-days-present" />
            <Metric value={`${d.attendance.total_minutes}`} label="Total active minutes" testId="report-total-minutes" />
          </div>
        </Block>
        <Block label="Mindfulness growth">
          <div className="grid grid-cols-2 gap-3">
            <Metric value={d.mindfulness.mindful_minutes} label="Mindful minutes" testId="report-mindful-minutes" />
            <Metric value={`${d.mindfulness.mood_lift > 0 ? "+" : ""}${d.mindfulness.mood_lift}`} label="Avg mood lift per lesson" testId="report-mood-lift" />
          </div>
        </Block>
      </div>

      {d.highlight && (
        <Block label="Self-discovery highlight">
          <blockquote className="rounded-2xl bg-[#FBF3EE] border-l-4 border-[#C87D53] p-5" data-testid="report-highlight">
            <p className="font-display text-lg text-[#2C4A3E] italic">"{d.highlight.reflection}"</p>
            <p className="text-xs text-stone-500 mt-2">{d.highlight.subject_name} · {d.highlight.topic}</p>
          </blockquote>
        </Block>
      )}

      <Block label="Teacher comments">
        {d.comments.filter((c) => c.comment).map((c) => (
          <div key={c.class_id} className="mb-3" data-testid={`report-comment-${c.class_id}`}>
            <p className="text-stone-700 leading-relaxed">{c.comment}</p>
            <p className="text-xs text-stone-500 mt-1">{c.teacher_name} · {c.class_name}</p>
          </div>
        ))}
        {!d.comments.some((c) => c.comment) && <p className="text-sm text-stone-400">No comments yet.</p>}
      </Block>
      <p className="text-[10px] text-stone-400 mt-10">Generated {new Date(d.generated_at).toLocaleString()}</p>
    </RecordShell>
  );
}
